import { SCREEN_W, HORIZON_Y } from "../shared/constants";
import { PAL } from "./palette";
import { SPRITE_DATA } from "./sceneryPng";


export type SceneryKind = "trees" | "harbour" | "city" | "tuscany" | "mountains" | "jungle" | "sunrise" | "forest" | "hills" | "desert" | "seaside" | "airport" | "savanna" | "quebec" | "montreal" | "paris" | "rome" | "toronto" | "manx" | "karting" | "rio" | "paques" | "hawaii" | "tokyo";

const H = HORIZON_Y;
const W = SCREEN_W;

function hash(n: number) {
  n = Math.imul(n ^ (n >>> 15), 2246822519);
  n = Math.imul(n ^ (n >>> 13), 3266489917);
  return (n ^ (n >>> 16)) >>> 0;
}
const mod = (a: number, b: number) => ((a % b) + b) % b;

/** Repeating cells scrolling with the camera: calls draw(cx, hash) for every cell crossing the screen. */
function cells(scroll: number, cell: number, salt: number, draw: (cx: number, h: number) => void) {
  const base = Math.floor(scroll);
  const off = mod(base, cell);
  const first = Math.floor(base / cell);
  for (let k = -2; k < W / cell + 3; k++) draw(Math.round(k * cell - off), hash(first + k + salt * 1013));
}

/** Upper half of an ellipse standing on the line y = cy. */
function dome(ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number, c: string) {
  ctx.fillStyle = c;
  for (let dy = 0; dy < ry; dy++) {
    const half = Math.round(rx * Math.sqrt(1 - ((ry - dy - 0.5) / ry) ** 2) + 0.0001);
    ctx.fillRect(cx - half, cy - ry + dy + 0, half * 2, 1);
  }
  void 0;
}
function rect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, c: string) {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x), Math.round(y), Math.max(0, Math.round(w)), Math.max(0, Math.round(h)));
}
function triangle(ctx: CanvasRenderingContext2D, cx: number, baseY: number, halfW: number, h: number, c: string) {
  ctx.fillStyle = c;
  for (let dy = 0; dy < h; dy++) {
    const half = Math.round((halfW * (dy + 1)) / h);
    ctx.fillRect(cx - half, baseY - h + dy, half * 2, 1);
  }
}
function clouds(ctx: CanvasRenderingContext2D, scroll: number) {
  cells(scroll * 0.15, 76, 9, (cx, h) => {
    const y = 8 + (h % 12), w = 14 + (h % 10);
    dome(ctx, cx, y + 4, w / 2, 4, PAL[1]);
    dome(ctx, cx + 7, y + 4, w / 3, 3, PAL[1]);
    rect(ctx, cx - w / 2, y + 4, w + 6, 1, PAL[1]);
  });
}
function dither(ctx: CanvasRenderingContext2D, y0: number, y1: number, c: string) {
  // denser towards y1: 25 % → 100 %
  ctx.fillStyle = c;
  for (let y = y0; y < y1; y++) {
    const d = (y - y0) / Math.max(1, y1 - y0);
    for (let x = (y & 1) * 1; x < W; x += 2) ctx.fillRect(x, y, 1, 1);
    if (d > 0.5) for (let x = 1 - (y & 1); x < W; x += 2) ctx.fillRect(x, y, 1, 1);
  }
}

/** A gull seen from the front: wings up or down. */
function gull(ctx: CanvasRenderingContext2D, x: number, y: number, up: boolean) {
  const c = PAL[3];
  if (up) {
    rect(ctx, x - 3, y - 1, 2, 1, c); rect(ctx, x - 1, y, 2, 1, c); rect(ctx, x + 1, y, 2, 1, c); rect(ctx, x + 3, y - 1, 2, 1, c);
  } else {
    rect(ctx, x - 3, y + 1, 2, 1, c); rect(ctx, x - 1, y, 2, 1, c); rect(ctx, x + 1, y, 2, 1, c); rect(ctx, x + 3, y + 1, 2, 1, c);
  }
}

/** One landmark crossing the screen: it is always on screen somewhere (the next copy enters on the right exactly as this one leaves on the left).
 * `a` is its centre at scroll 0, `v` its scroll speed, `hw` its half width. draw(cx) paints it around its centre. */
function landmark(s: number, a: number, v: number, hw: number, draw: (cx: number) => void) {
  const P = W + 2 * hw;
  draw(Math.round(mod(a - s * v + hw, P) - hw));
}


function line(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, c: string) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
  for (let i = 0; i <= n; i++) rect(ctx, x0 + ((x1 - x0) * i) / n, y0 + ((y1 - y0) * i) / n, 1, 1, c);
}

/** Landmarks are PNG images (assets/scenery/*.png, editable in Paint): left edge at cx + SPRITE_OX, bottom on the horizon. */
export const SPRITE_OX: Record<string, number> = { bridge: -60, laporte: -70, chateau: -95, mount: -95, downtown: -56, stadium: -44, laronde: -44, eiffel: -12, arc: -16, notredame: -17, sacre: -34, colisee: -28, pantheon: -13, vittoriano: -31, trajan: -5, peters: -28, cntower: -7, rogers: -31, torontoskyline: -56 };
const images: Record<string, HTMLCanvasElement> = {};
const PAL_RGB = PAL.map((h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)));
/** Cleans an image edited in any program: transparent, white or magenta pixels become see-through, every other pixel snaps to the nearest of the 4 game colours. */
function prepare(im: HTMLImageElement): HTMLCanvasElement {
  const cv = document.createElement("canvas");
  cv.width = im.naturalWidth; cv.height = im.naturalHeight;
  const c = cv.getContext("2d")!;
  c.drawImage(im, 0, 0);
  const d = c.getImageData(0, 0, cv.width, cv.height), px = d.data;
  for (let i = 0; i < px.length; i += 4) {
    const r = px[i], g = px[i + 1], b = px[i + 2];
    if (px[i + 3] < 128 || (r > 250 && g > 250 && b > 250) || (r > 250 && g < 5 && b > 250)) { px[i + 3] = 0; continue; }
    let best = 0, bd = 1e9;
    for (let k = 0; k < 4; k++) { const e = (r - PAL_RGB[k][0]) ** 2 + (g - PAL_RGB[k][1]) ** 2 + (b - PAL_RGB[k][2]) ** 2; if (e < bd) { bd = e; best = k; } }
    px[i] = PAL_RGB[best][0]; px[i + 1] = PAL_RGB[best][1]; px[i + 2] = PAL_RGB[best][2]; px[i + 3] = 255;
  }
  c.putImageData(d, 0, 0);
  return cv;
}
if (typeof Image !== "undefined") for (const [k, uri] of Object.entries(SPRITE_DATA)) { const im = new Image(); im.onload = () => { images[k] = prepare(im); }; im.src = uri; }
/** "Couleurs" option: landmarks in warm stone tones instead of the 4 game shades (set by renderRace). */
export const sceneryTint = { on: false };
const STONE = [[0xf6, 0xef, 0xd8], [0xd8, 0xc4, 0x9a], [0x8a, 0x7a, 0x68], [0x2c, 0x30, 0x40]]; // lightest .. darkest
const tinted: Record<string, HTMLCanvasElement> = {};
function tintOf(name: string): HTMLCanvasElement | undefined {
  const src = images[name];
  if (!src) return undefined;
  if (tinted[name]) return tinted[name];
  const cv = document.createElement("canvas");
  cv.width = src.width; cv.height = src.height;
  const c = cv.getContext("2d")!;
  c.drawImage(src, 0, 0);
  const d = c.getImageData(0, 0, cv.width, cv.height), px = d.data;
  for (let i = 0; i < px.length; i += 4) {
    if (!px[i + 3]) continue;
    let best = 0, bd = 1e9;
    for (let k = 0; k < 4; k++) { const e = (px[i] - PAL_RGB[k][0]) ** 2 + (px[i + 1] - PAL_RGB[k][1]) ** 2 + (px[i + 2] - PAL_RGB[k][2]) ** 2; if (e < bd) { bd = e; best = k; } }
    px[i] = STONE[best][0]; px[i + 1] = STONE[best][1]; px[i + 2] = STONE[best][2];
  }
  c.putImageData(d, 0, 0);
  return (tinted[name] = cv);
}
function sprite(name: string, ctx: CanvasRenderingContext2D, cx: number) {
  const im = (sceneryTint.on && tintOf(name)) || images[name];
  if (im) ctx.drawImage(im, Math.round(cx + SPRITE_OX[name]), H - im.height);
  else LEGACY[name](ctx, cx);
}

/** The original code drawings of the landmarks: used to export the editable PNGs (tools/scenery-export) and as a fallback while a PNG is not loaded. */
export const LEGACY: Record<string, (ctx: CanvasRenderingContext2D, cx: number) => void> = {
  laporte: () => {}, // PNG only: nothing is drawn while it loads
  bridge: (ctx, bx) => { // Quebec Bridge: cantilever steel truss on two piers with a suspended centre span
    const top = (a: number) => (a <= 14 ? 10 : a <= 38 ? 10 + ((a - 14) * 12) / 24 : Math.max(3, 22 - ((a - 38) * 19) / 22));
    rect(ctx, bx - 60, H - 14, 120, 2, PAL[3]); // deck
    for (let dx = -60; dx <= 60; dx++) {
      const th = Math.round(top(Math.abs(dx)));
      rect(ctx, bx + dx, H - 14 - th, 1, 1, PAL[3]); // top chord
      if (dx % 3 === 0) rect(ctx, bx + dx, H - 14 - th, 1, th, PAL[3]); // verticals
      else if (dx % 3 === 1 && (dx >> 1) % 2 === 0) rect(ctx, bx + dx, H - 14 - (th >> 1), 1, 1, PAL[3]); // bracing
    }
    for (const px of [-38, 38]) rect(ctx, bx + px - 2, H - 12, 5, 12, PAL[3]); // piers
  },
  chateau: (ctx, mid) => { 
    const cx = mid - 33; // the chateau; the cliff runs from cx-62 to cx+128
    rect(ctx, cx - 62, H - 9, 190, 9, PAL[2]); // cliff
    for (let x = -62; x < 128; x += 5) rect(ctx, cx + x, H - 9 - ((x >> 2) & 1), 3, 1, PAL[2]);
    // Chateau Frontenac
    rect(ctx, cx - 44, H - 21, 88, 12, PAL[1]);
    rect(ctx, cx - 45, H - 24, 90, 3, PAL[3]); // long copper roof
    for (let x = -41; x < 42; x += 4) { rect(ctx, cx + x, H - 18, 1, 2, PAL[3]); rect(ctx, cx + x, H - 13, 1, 2, PAL[3]); }
    for (const tx of [-44, -22, 22, 44]) { // turrets
      rect(ctx, cx + tx - 3, H - 27, 6, 6, PAL[1]);
      triangle(ctx, cx + tx, H - 27, 5, 8, PAL[3]);
    }
    rect(ctx, cx - 7, H - 31, 14, 10, PAL[1]); // central tower
    rect(ctx, cx - 2, H - 29, 1, 3, PAL[3]); rect(ctx, cx + 1, H - 29, 1, 3, PAL[3]);
    triangle(ctx, cx, H - 31, 9, 9, PAL[3]);
    rect(ctx, cx, H - 43, 1, 3, PAL[3]); // spire
    // Hotel Concorde: a wide pale tower with its revolving restaurant dome
    const hx = cx + 105;
    rect(ctx, hx - 6, H - 30, 12, 21, PAL[1]);
    rect(ctx, hx - 6, H - 30, 2, 21, PAL[2]);
    for (let y = H - 28; y < H - 10; y += 3) for (let x = hx - 2; x < hx + 5; x += 3) rect(ctx, x, y, 2, 1, PAL[3]);
    rect(ctx, hx - 9, H - 34, 18, 4, PAL[3]); // restaurant ring
    rect(ctx, hx - 8, H - 33, 16, 1, PAL[0]);
    dome(ctx, hx, H - 34, 7, 5, PAL[3]);
    rect(ctx, hx, H - 43, 1, 4, PAL[3]);
  },
  laronde: (ctx, cx) => { // La Ronde: the big wheel (La Grande Roue) and the wooden-look coaster (Le Monstre) on Ile Sainte-Helene
    const wx = cx - 22, wy = H - 19, r = 16;
    rect(ctx, cx - 44, H - 2, 88, 2, PAL[2]); // island shore
    for (const sx of [-9, 9]) line(ctx, wx, wy, wx + sx, H - 2, PAL[3]); // A-frame legs
    for (let i = 0; i < 120; i++) { // rim
      const a = (i / 120) * Math.PI * 2;
      rect(ctx, wx + Math.cos(a) * r, wy + Math.sin(a) * r, 1, 1, PAL[3]);
    }
    for (let i = 0; i < 8; i++) { // spokes and gondolas
      const a = (i / 8) * Math.PI * 2 + 0.2;
      line(ctx, wx, wy, wx + Math.cos(a) * r, wy + Math.sin(a) * r, PAL[1]);
      rect(ctx, wx + Math.cos(a) * (r + 1) - 1, wy + Math.sin(a) * (r + 1), 3, 2, PAL[3]);
    }
    rect(ctx, wx - 1, wy - 1, 3, 3, PAL[3]); // hub
    // coaster: a rolling track on a lattice of posts
    const trackY = (x: number) => Math.round(H - 8 - 15 * Math.max(0, Math.sin((x - 6) / 6.2)) - 5 * Math.max(0, Math.sin((x - 3) / 3.1)) * (x > 20 ? 1 : 0));
    for (let x = 6; x <= 38; x++) {
      const y = trackY(x);
      rect(ctx, cx + x, y, 1, 2, PAL[3]); // the rail
      if (x % 3 === 0) rect(ctx, cx + x, y + 2, 1, H - 2 - y - 2, PAL[1]); // posts
      else if (x % 3 === 1) rect(ctx, cx + x, y + 2 + ((x >> 1) % 2) * 3, 1, 1, PAL[2]); // cross bracing
    }
  },
  eiffel: (ctx, cx) => { // Eiffel Tower: four curved legs joined by two platforms, lattice above, antenna
    const hwAt = (y: number) => Math.round(1 + 11 * Math.exp(-y / 10));
    for (let y = 0; y < 38; y++) {
      const hw = hwAt(y), py = H - 1 - y;
      rect(ctx, cx - hw, py, y < 12 ? 2 : 1, 1, PAL[3]);
      rect(ctx, cx + hw - (y < 12 ? 1 : 0), py, y < 12 ? 2 : 1, 1, PAL[3]);
      if (y < 9) { // under the first arch: only the legs
        const inner = Math.max(2, hw - 4);
        rect(ctx, cx - inner, py, 1, 1, PAL[3]);
        rect(ctx, cx + inner, py, 1, 1, PAL[3]);
      } else for (let x = -hw + 1; x < hw; x++) if ((x + y) % 4 === 0 || (x - y) % 4 === 0) rect(ctx, cx + x, py, 1, 1, PAL[2]); // lattice cross-bracing
    }
    for (let a = 0; a < 4; a++) { // the great arch between the legs
      const hw = hwAt(9 + a) - 3 + a;
      rect(ctx, cx - hw, H - 10 - a, 2 * hw + 1, 1, PAL[3]);
    }
    for (const py of [12, 24]) rect(ctx, cx - hwAt(py) - 2, H - 1 - py, 2 * hwAt(py) + 5, 2, PAL[3]); // platforms
    rect(ctx, cx - 1, H - 40, 3, 3, PAL[3]);
    rect(ctx, cx, H - 44, 1, 4, PAL[3]); // antenna
  },
  arc: (ctx, cx) => { // Arc de Triomphe: massive block, tall central arch, small side arches and the sculpted attic
    rect(ctx, cx - 14, H - 24, 28, 24, PAL[2]);
    rect(ctx, cx - 15, H - 27, 30, 3, PAL[3]); // attic
    for (let x = -13; x < 14; x += 3) rect(ctx, cx + x, H - 26, 1, 1, PAL[1]); // its relief
    rect(ctx, cx - 14, H - 23, 28, 1, PAL[3]);
    dome(ctx, cx, H - 8, 5, 5, PAL[1]); // the great arch (seen through)
    rect(ctx, cx - 5, H - 9, 10, 9, PAL[1]);
    rect(ctx, cx - 5, H - 14, 1, 14, PAL[3]); rect(ctx, cx + 5, H - 14, 1, 14, PAL[3]);
    for (const sx of [-11, 8]) { rect(ctx, cx + sx, H - 6, 3, 6, PAL[3]); rect(ctx, cx + sx, H - 7, 3, 1, PAL[3]); } // small arches
    for (const sx of [-12, 9]) rect(ctx, cx + sx, H - 20, 4, 8, PAL[3]); // sculpted panels
    rect(ctx, cx - 16, H - 1, 32, 1, PAL[3]); // base
  },
  notredame: (ctx, cx) => { // Notre-Dame: the two towers of the west front, rose window, three portals, the nave with its flying buttresses and the spire
    rect(ctx, cx + 8, H - 14, 22, 14, PAL[2]); // nave
    rect(ctx, cx + 7, H - 17, 24, 3, PAL[3]); // its roof
    for (let i = 0; i < 4; i++) { line(ctx, cx + 11 + i * 5, H - 13, cx + 9 + i * 5, H, PAL[3]); line(ctx, cx + 11 + i * 5, H - 13, cx + 13 + i * 5, H - 11, PAL[3]); } // flying buttresses
    triangle(ctx, cx + 19, H - 17, 3, 21, PAL[3]); // the spire
    rect(ctx, cx + 19, H - 41, 1, 3, PAL[3]);
    rect(ctx, cx - 6, H - 21, 8, 21, PAL[1]); // the front between the towers
    for (const tx of [-16, 1]) { // the towers
      rect(ctx, cx + tx, H - 28, 9, 28, PAL[1]);
      rect(ctx, cx + tx + 7, H - 28, 2, 28, PAL[2]); // shaded side
      rect(ctx, cx + tx - 1, H - 31, 11, 3, PAL[3]); // crown
      for (let k = 0; k < 3; k++) rect(ctx, cx + tx + k * 4, H - 33, 2, 2, PAL[3]);
      rect(ctx, cx + tx + 2, H - 25, 2, 8, PAL[3]); rect(ctx, cx + tx + 5, H - 25, 2, 8, PAL[3]); // louvre openings
    }
    rect(ctx, cx - 6, H - 21, 8, 1, PAL[3]); // gallery of kings
    for (let a = 0; a < 16; a++) rect(ctx, cx - 2 + Math.cos((a / 16) * 6.283) * 3, H - 13 + Math.sin((a / 16) * 6.283) * 3, 1, 1, PAL[3]); // rose window
    rect(ctx, cx - 2, H - 13, 1, 1, PAL[3]);
    for (const px of [-14, -3, 4]) { rect(ctx, cx + px, H - 6, 3, 6, PAL[3]); rect(ctx, cx + px + 1, H - 7, 1, 1, PAL[3]); } // portals
  },
  sacre: (ctx, cx) => { // Sacre-Coeur on its hill: the great white dome and its small ones
    dome(ctx, cx, H, 34, 5, PAL[1]); // the hill
    rect(ctx, cx - 12, H - 9, 24, 5, PAL[2]);
    for (const dx of [-9, 9]) dome(ctx, cx + dx, H - 9, 3, 4, PAL[2]);
    dome(ctx, cx, H - 9, 6, 8, PAL[2]);
    rect(ctx, cx, H - 20, 1, 3, PAL[3]);
    for (let x = -10; x <= 10; x += 4) rect(ctx, cx + x, H - 8, 1, 3, PAL[1]);
  },
  colisee: (ctx, cx) => { // Colosseum: three tiers of arches and the attic on the whole side, the outer ring broken away on the left
    const topAt = (x: number) => (x > 6 ? 22 : x > -4 ? 22 - Math.floor((6 - x) / 2.5) : 12 + (((x + 28) >> 2) % 3)); // outer wall height: ruined on the left
    for (let x = -28; x < 28; x++) {
      const t = topAt(x);
      rect(ctx, cx + x, H - t, 1, t, x < -16 ? PAL[2] : PAL[1]);
    }
    for (let x = -28; x < 28; x += 4) { // arches, one tier above the other
      for (let tier = 0; tier < 3; tier++) {
        const y0 = tier * 6;
        if (topAt(x) < y0 + 6) continue;
        rect(ctx, cx + x + 1, H - y0 - 5, 2, 4, PAL[3]);
        rect(ctx, cx + x + 1, H - y0 - 6, 2, 1, PAL[3]);
      }
      if (topAt(x) >= 22) rect(ctx, cx + x + 1, H - 21, 2, 2, PAL[3]); // attic windows
    }
    rect(ctx, cx + 6, H - 23, 22, 1, PAL[3]); // cornice
    for (let x = -4; x < 6; x++) rect(ctx, cx + x, H - topAt(x) - 1, 1, 1, PAL[3]); // broken edge
    rect(ctx, cx - 28, H - 1, 56, 1, PAL[3]);
    for (let y = 6; y <= 18; y += 6) rect(ctx, cx - 28, H - y, 56, 1, PAL[2]); // cornices between tiers
  },
  pantheon: (ctx, cx) => { // Pantheon: the great dome and its drum behind a portico of columns under a triangular pediment
    dome(ctx, cx, H - 16, 11, 9, PAL[1]);
    rect(ctx, cx, H - 25, 1, 1, PAL[3]); // oculus
    rect(ctx, cx - 11, H - 16, 22, 5, PAL[2]); // drum
    rect(ctx, cx - 12, H - 12, 24, 12, PAL[1]); // portico
    for (let x = -11; x <= 10; x += 3) rect(ctx, cx + x, H - 11, 2, 11, PAL[2]); // eight columns
    rect(ctx, cx - 13, H - 13, 26, 1, PAL[3]); // entablature
    triangle(ctx, cx, H - 14, 13, 5, PAL[1]); // pediment
    triangle(ctx, cx, H - 14, 8, 3, PAL[2]);
    rect(ctx, cx - 13, H - 14, 26, 1, PAL[3]);
  },
  vittoriano: (ctx, cx) => { // Vittoriano: the white marble monument, great stairs, two colonnades and the quadrigas on the corners
    rect(ctx, cx - 30, H - 6, 60, 6, PAL[1]); // base and stairs
    for (let x = -30; x < 30; x += 3) rect(ctx, cx + x, H - 3, 2, 1, PAL[2]);
    rect(ctx, cx - 14, H - 14, 28, 8, PAL[1]); // central terrace
    rect(ctx, cx - 14, H - 14, 28, 1, PAL[2]);
    for (const w of [-1, 1]) { // the two colonnades
      const x0 = w < 0 ? cx - 30 : cx + 14;
      rect(ctx, x0, H - 24, 16, 18, PAL[1]);
      rect(ctx, x0 - 1, H - 26, 18, 2, PAL[2]);
      for (let x = 1; x < 16; x += 3) rect(ctx, x0 + x, H - 22, 1, 15, PAL[2]);
      rect(ctx, x0 + 4, H - 31, 8, 5, PAL[2]); // quadriga: chariot and horses
      rect(ctx, x0 + 3, H - 29, 2, 2, PAL[3]); rect(ctx, x0 + 11, H - 29, 2, 2, PAL[3]);
      rect(ctx, x0 + 7, H - 34, 2, 3, PAL[3]);
    }
    rect(ctx, cx - 3, H - 20, 6, 6, PAL[2]); // the equestrian monument in the middle
    rect(ctx, cx - 2, H - 22, 4, 2, PAL[3]);
    rect(ctx, cx - 1, H - 25, 2, 3, PAL[3]);
  },
  trajan: (ctx, cx) => { // Trajan's Column: pedestal, spiral frieze, capital and statue
    rect(ctx, cx - 4, H - 6, 9, 6, PAL[1]);
    rect(ctx, cx - 5, H - 6, 11, 1, PAL[2]);
    rect(ctx, cx - 2, H - 32, 5, 26, PAL[1]);
    for (let y = 0; y < 24; y++) rect(ctx, cx - 2 + ((y * 2) % 5), H - 8 - y, 1, 1, PAL[2]); // the spiral
    rect(ctx, cx - 4, H - 34, 9, 2, PAL[2]);
    rect(ctx, cx - 1, H - 38, 3, 4, PAL[3]);
  },
  peters: (ctx, cx) => { // Saint Peter's: the great dome with its ribs and lantern, seen far away over the roofs
    dome(ctx, cx, H, 28, 4, PAL[1]);
    rect(ctx, cx - 12, H - 7, 24, 5, PAL[2]);
    dome(ctx, cx, H - 7, 8, 9, PAL[2]);
    for (let x = -6; x <= 6; x += 3) rect(ctx, cx + x, H - 14, 1, 7, PAL[1]); // ribs
    rect(ctx, cx - 1, H - 19, 3, 3, PAL[3]); // lantern
    rect(ctx, cx, H - 23, 1, 4, PAL[3]);
  },
  mount: (ctx, mx) => { 
    dome(ctx, mx, H, 95, 17, PAL[1]);
    rect(ctx, mx + 8, H - 26, 1, 8, PAL[3]); // the cross on the summit
    rect(ctx, mx + 6, H - 23, 5, 1, PAL[3]);
  },
  downtown: (ctx, dx) => { // downtown: the towers, each a little different
    const tower = (x: number, w: number, hh: number, c: string) => {
      rect(ctx, dx + x, H - hh, w, hh, c);
      for (let y = H - hh + 2; y < H - 1; y += 3) for (let wx = x + 1; wx < x + w - 1; wx += 2) if (c === PAL[3] && (hash(wx * 31 + y * 7) & 3) !== 0) rect(ctx, dx + wx, y, 1, 1, PAL[1]);
    };
    tower(-54, 9, 16, PAL[2]); tower(-44, 8, 22, PAL[3]); tower(-35, 8, 18, PAL[2]);
    tower(-26, 10, 29, PAL[3]); triangle(ctx, dx - 21, H - 29, 5, 8, PAL[3]); // the pyramid-topped tower
    tower(-15, 9, 34, PAL[3]); rect(ctx, dx - 13, H - 37, 5, 3, PAL[3]); rect(ctx, dx - 11, H - 40, 1, 3, PAL[3]); // the cross-shaped tower: crown and beacon
    tower(-5, 8, 23, PAL[2]); tower(4, 9, 31, PAL[3]); tower(15, 8, 20, PAL[2]); tower(25, 9, 25, PAL[3]); tower(36, 8, 15, PAL[2]); tower(45, 9, 21, PAL[3]);
    rect(ctx, dx - 56, H - 2, 112, 2, PAL[3]); // quay
  },
  stadium: (ctx, cx) => { // Olympic Stadium: low bowl, ring roof and the inclined tower held by cables
    dome(ctx, cx, H - 4, 44, 9, PAL[2]);
    rect(ctx, cx - 44, H - 4, 88, 4, PAL[3]);
    rect(ctx, cx - 34, H - 11, 68, 1, PAL[3]);
    for (let x = -40; x <= 40; x += 6) rect(ctx, cx + x, H - 3, 1, 3, PAL[1]);
    const tx = cx + 34, ty = H - 4, ux = cx + 14, uy = H - 40; // base and top of the tower
    for (let k = -1; k <= 1; k++) line(ctx, tx + k, ty, ux + k, uy, PAL[3]);
    for (const bx2 of [-36, -20, -4, 8]) line(ctx, ux, uy + 2, cx + bx2, H - 11, PAL[3]); // cables
    rect(ctx, ux - 2, uy - 2, 5, 3, PAL[3]);
  },
  cntower: (ctx, cx) => { // CN Tower: tripod base, slim shaft, main pod, sky pod and antenna
    for (let k = 0; k < 3; k++) { line(ctx, cx - 4 + k, H, cx - 1, H - 14, PAL[2]); line(ctx, cx + 4 - k, H, cx + 1, H - 14, PAL[2]); } // legs
    rect(ctx, cx - 1, H - 26, 3, 14, PAL[3]); // shaft up to the pod
    rect(ctx, cx - 1, H - 20, 1, 1, PAL[3]);
    dome(ctx, cx, H - 25, 7, 3, PAL[3]); rect(ctx, cx - 7, H - 25, 15, 2, PAL[3]); rect(ctx, cx - 5, H - 23, 11, 1, PAL[3]); // main pod
    for (let x = -5; x <= 5; x += 2) rect(ctx, cx + x, H - 24, 1, 1, PAL[1]); // pod windows
    rect(ctx, cx, H - 31, 1, 6, PAL[3]); // shaft above the pod
    rect(ctx, cx - 1, H - 33, 3, 3, PAL[3]); rect(ctx, cx - 1, H - 32, 3, 1, PAL[1]); // sky pod
    rect(ctx, cx, H - 39, 1, 6, PAL[3]); // antenna
  },
  rogers: (ctx, cx) => { // Rogers Centre: the retractable dome, its ribs and the concrete rim
    dome(ctx, cx, H - 5, 30, 13, PAL[2]);
    for (let k = -3; k <= 3; k++) line(ctx, cx + k * 9, H - 5, cx + Math.round(k * 3), H - 18, PAL[3]); // roof ribs
    rect(ctx, cx - 30, H - 6, 61, 1, PAL[3]);
    rect(ctx, cx - 31, H - 5, 63, 5, PAL[3]);
    for (let x = -28; x <= 28; x += 4) rect(ctx, cx + x, H - 4, 2, 2, PAL[1]); // concourse windows
    rect(ctx, cx + 24, H - 18, 3, 12, PAL[3]); rect(ctx, cx + 22, H - 18, 7, 2, PAL[3]); // light tower
  },
  torontoskyline: (ctx, dx) => { // Toronto downtown: black bank towers, stepped crowns and glass slabs
    const win = (x: number, w: number, hh: number, c: string) => {
      for (let y = H - hh + 2; y < H - 1; y += 3) for (let wx = x + 1; wx < x + w - 1; wx += 2) if (c === PAL[3] && (hash(wx * 29 + y * 11) & 3) !== 0) rect(ctx, dx + wx, y, 1, 1, PAL[1]);
    };
    const box = (x: number, w: number, hh: number, c: string) => { rect(ctx, dx + x, H - hh, w, hh, c); win(x, w, hh, c); };
    box(-56, 9, 15, PAL[2]); box(-46, 10, 24, PAL[3]); box(-35, 9, 19, PAL[2]);
    box(-25, 12, 35, PAL[3]); rect(ctx, dx - 22, H - 37, 6, 2, PAL[3]); rect(ctx, dx - 20, H - 39, 2, 2, PAL[3]); // tall slab with a notched top
    box(-12, 10, 28, PAL[2]); triangle(ctx, dx - 7, H - 28, 5, 5, PAL[2]); // pitched crown
    box(0, 12, 31, PAL[3]); rect(ctx, dx + 2, H - 34, 8, 3, PAL[3]); rect(ctx, dx + 4, H - 36, 4, 2, PAL[3]); // stepped crown
    box(14, 8, 21, PAL[2]); box(23, 11, 26, PAL[3]); box(35, 8, 17, PAL[2]); box(44, 10, 22, PAL[3]);
    rect(ctx, dx - 56, H - 2, 112, 2, PAL[3]); // waterfront
  },
};

/** `t` is the frame counter: it drives the things that move on their own (birds, boats, aircraft). */
export function drawScenery(ctx: CanvasRenderingContext2D, kind: SceneryKind, s: number, t = 0): boolean {
  switch (kind) {
    case "city": {
      clouds(ctx, s);
      // far skyline, lighter
      cells(s * 0.5, 14, 1, (cx, h) => {
        const w = 10 + (h % 4), hh = 14 + ((h >> 3) % 14);
        rect(ctx, cx, H - hh, w, hh, PAL[1]);
        for (let y = H - hh + 2; y < H - 2; y += 3) for (let x = cx + 2; x < cx + w - 1; x += 3) rect(ctx, x, y, 1, 1, PAL[0]);
        if (h % 5 === 0) rect(ctx, cx + w / 2, H - hh - 5, 1, 5, PAL[1]);
      });
      // near skyline, darker with lit windows
      cells(s, 13, 2, (cx, h) => {
        const w = 8 + (h % 5), hh = 8 + ((h >> 4) % 15);
        rect(ctx, cx, H - hh, w, hh, PAL[3]);
        for (let y = H - hh + 2; y < H - 1; y += 3)
          for (let x = cx + 1; x < cx + w - 1; x += 2) if ((hash(h + x * 7 + y) & 3) !== 0) rect(ctx, x, y, 1, 1, PAL[1]);
        if (h % 7 === 0) {
          rect(ctx, cx + w / 2 - 1, H - hh - 7, 2, 7, PAL[3]); // tower
          rect(ctx, cx + w / 2, H - hh - 11, 1, 4, PAL[3]);
        }
      });
      return true;
    }
    case "harbour": {
      // Australia: a harbour city with a steel-arch bridge and sail-roofed opera house, eucalyptus scrub in front
      clouds(ctx, s);
      dither(ctx, H - 5, H, PAL[1]); // water
      // distant skyline
      cells(s * 0.22, 13, 60, (cx, h) => {
        if (h % 4 === 0) return;
        const w = 7 + (h % 5), hh = 6 + ((h >> 3) % 9);
        rect(ctx, cx, H - hh, w, hh, PAL[1]);
        if (h % 6 === 1) rect(ctx, cx + (w >> 1), H - hh - 4, 1, 4, PAL[1]); // mast
      });
      // the landmarks come as one set per 300 px of scrolling: the bridge, then the opera house to its right
      const P = 300, base = Math.round(mod(60 - s * 0.3, P) - 120);
      for (const off of [-P, 0, P]) {
        const cx = base + off;
        if (cx < -100 || cx > W + 150) continue;
        const archY = (dx: number) => H - (8 + 26 * (1 - (dx / 62) ** 2));
        for (let dx = -62; dx <= 62; dx++) {
          const y = Math.round(archY(dx));
          if (dx % 6 === 0 && dx > -58 && dx < 58) rect(ctx, cx + dx, y + 2, 1, H - 8 - y - 2, PAL[2]); // hangers
          rect(ctx, cx + dx, y, 1, 2, PAL[3]);
        }
        rect(ctx, cx - 80, H - 8, 160, 2, PAL[3]); // deck
        for (const sx of [-65, 61]) {
          rect(ctx, cx + sx, H - 27, 5, 27, PAL[2]); // pylons
          rect(ctx, cx + sx - 1, H - 29, 7, 2, PAL[3]);
        }
        // opera house: nested shells leaning towards the water
        const ox = cx + 150;
        rect(ctx, ox - 28, H - 3, 58, 3, PAL[3]); // podium
        const shell = (x0: number, w: number, hh: number, dir: number) => {
          for (let i = 0; i < w; i++) {
            const colH = Math.round(hh * Math.sqrt(Math.max(0, 1 - (i / w) ** 2)));
            const x = dir > 0 ? x0 + i : x0 - i;
            rect(ctx, x, H - 3 - colH, 1, colH, i % 3 === 1 ? PAL[0] : PAL[1]); // pale ribs on a mid-tone shell
            rect(ctx, x, H - 3 - colH, 1, i === 0 ? colH : 1, PAL[3]);
          }
        };
        shell(ox - 24, 19, 19, 1);
        shell(ox - 15, 15, 14, 1);
        shell(ox - 8, 10, 9, 1);
        shell(ox + 30, 17, 16, -1);
        shell(ox + 22, 13, 11, -1);
        shell(ox + 15, 8, 7, -1);
      }
      // eucalyptus scrub close to the road
      cells(s * 0.7, 19, 61, (cx, h) => {
        dome(ctx, cx, H, 9 + (h % 4), 4 + ((h >> 3) % 3), PAL[2]);
      });
      cells(s, 34, 62, (cx, h) => {
        if (h % 3 === 0) return;
        const ht = 11 + (h % 7), lean = (h >> 5) % 3 - 1;
        ctx.fillStyle = PAL[3];
        for (let y = 0; y < ht; y++) ctx.fillRect(cx + Math.round((lean * y) / 5), H - y, 2, 1); // slightly crooked trunk
        const tx = cx + Math.round((lean * ht) / 5);
        rect(ctx, tx + 2, H - ht + 3, 4, 1, PAL[3]); // side branch
        // irregular leafy crown with gaps: eucalyptus leaves hang loosely
        dome(ctx, tx - 3, H - ht + 5, 4, 3, PAL[2]);
        dome(ctx, tx + 6, H - ht + 4, 4, 3, PAL[2]);
        dome(ctx, tx + 1, H - ht + 1, 5, 4, PAL[2]);
        rect(ctx, tx - 1, H - ht + 1, 2, 1, PAL[3]);
      });
      return true;
    }
    case "tuscany": {
      // Italy: jagged snow-capped Alps far away, rolling hills with a hilltop village and bell tower, rows of cypresses
      clouds(ctx, s);
      const ridge = (wx: number) => {
        // irregular, asymmetric peaks: random height per 22 px cell, sharp saw-tooth between them
        const c = Math.floor(wx / 22), f = (wx - c * 22) / 22;
        const a = 12 + (hash(c + 301) % 18), b = 12 + (hash(c + 302) % 18);
        const peak = 0.35 + (hash(c + 303) % 40) / 100; // where in the cell the summit sits
        return f < peak ? a + ((b + 6 - a) * f) / peak : b + 6 - ((b + 6 - 11) * (f - peak)) / (1 - peak);
      };
      const far = s * 0.12;
      for (let x = 0; x < W; x++) {
        const wx = Math.floor(x + far);
        const hh = Math.round(ridge(wx));
        const next = Math.round(ridge(wx + 1));
        ctx.fillStyle = PAL[1];
        ctx.fillRect(x, H - hh, 1, hh);
        const snow = Math.round(hh * 0.45) - (((wx * 7) >> 1) & 1);
        ctx.fillStyle = PAL[0];
        ctx.fillRect(x, H - hh, 1, Math.max(0, snow));
        if (next < hh) {
          // slope facing away from the light
          ctx.fillStyle = PAL[2];
          ctx.fillRect(x, H - hh + Math.max(1, snow), 1, Math.max(0, hh - snow - 1));
        }
        // dark ridge line so the white snow reads against the sky
        ctx.fillStyle = PAL[2];
        ctx.fillRect(x, H - Math.max(hh, next), 1, Math.max(1, Math.abs(hh - next) + (next > hh ? 0 : 1)));
      }
      const hillH = (wx: number) => 7 + 3 * Math.sin(wx * 0.045) + 2 * Math.sin(wx * 0.11 + 1.3);
      const near = s * 0.55;
      const nb = Math.floor(near);
      for (let x = 0; x < W; x++) {
        const hh = Math.round(hillH(x + nb));
        rect(ctx, x, H - hh, 1, hh, PAL[2]);
        if (hh > 8 && ((x + nb) & 3) === 0) rect(ctx, x, H - hh, 1, 1, PAL[1]); // sunlit crest dots
      }
      // hilltop village: a few houses and a campanile
      cells(near, 96, 40, (cx, h) => {
        if (h % 3 === 0) return;
        const by = H - Math.round(hillH(cx + nb)) + 1;
        for (let i = 0; i < 4; i++) {
          const w = 7 + ((h >> (i * 3)) % 4), hh = 5 + ((h >> (i * 2 + 1)) % 3), x0 = cx - 18 + i * 11;
          rect(ctx, x0, by - hh, w, hh, PAL[0]);
          triangle(ctx, x0 + Math.floor(w / 2), by - hh, Math.ceil(w / 2) + 1, 4, PAL[3]);
          rect(ctx, x0 + 2, by - 3, 1, 2, PAL[3]);
        }
        rect(ctx, cx + 27, by - 22, 5, 22, PAL[0]); // campanile
        rect(ctx, cx + 28, by - 18, 3, 4, PAL[3]); // belfry opening
        triangle(ctx, cx + 29, by - 22, 4, 5, PAL[3]);
      });
      // cypresses along the crest
      cells(near, 11, 41, (cx, h) => {
        if (h % 3 === 0) return;
        const ht = 8 + (h % 5);
        const by = H - Math.round(hillH(cx + nb)) + 1;
        dome(ctx, cx, by, 2, ht, PAL[3]);
      });
      return true;
    }
    case "mountains": {
      clouds(ctx, s);
      cells(s * 0.35, 56, 3, (cx, h) => {
        const hh = 16 + (h % 11), hw = Math.round(hh * 1.25);
        triangle(ctx, cx, H, hw, hh, PAL[1]);
        // shaded right face
        ctx.fillStyle = PAL[2];
        for (let dy = 0; dy < hh; dy++) ctx.fillRect(cx + 1 + (dy % 5 === 0 ? 1 : 0), H - hh + dy, Math.round((hw * (dy + 1)) / hh) - 1, 1);
        // snow cap with a ragged lower edge
        const cap = Math.round(hh * 0.38);
        for (let dy = 0; dy < cap; dy++) {
          const half = Math.round((hw * (dy + 1)) / hh);
          const jag = dy > cap - 4 ? ((dy + cx) & 1) * 2 : 0;
          rect(ctx, cx - half + jag, H - hh + dy, half * 2 - jag * 2, 1, PAL[0]);
          rect(ctx, cx + 1, H - hh + dy, Math.max(0, half - jag - 1), 1, PAL[1]);
        }
      });
      cells(s * 0.8, 30, 4, (cx, h) => {
        triangle(ctx, cx, H, 12 + (h % 6), 8 + ((h >> 3) % 6), PAL[3]);
      });
      return true;
    }
    case "jungle": {
      // dense canopy mass
      cells(s * 0.6, 11, 5, (cx, h) => dome(ctx, cx, H, 9 + (h % 4), 10 + ((h >> 3) % 8), PAL[2]));
      cells(s * 0.8, 8, 6, (cx, h) => dome(ctx, cx, H, 6 + (h % 3), 5 + ((h >> 3) % 5), PAL[3]));
      // palms
      cells(s, 34, 7, (cx, h) => {
        if (h % 3 === 2) return;
        const ht = 20 + (h % 10), lean = ((h >> 4) % 3) - 1;
        ctx.fillStyle = PAL[3];
        for (let y = 0; y < ht; y++) ctx.fillRect(cx + Math.round((lean * y * y) / (ht * 3)), H - y, 2, 1);
        const tx = cx + Math.round((lean * ht) / 3), ty = H - ht;
        for (const d of [-1, 1]) {
          for (let k = 0; k < 3; k++) {
            const len = 9 - k * 2;
            for (let t = 0; t <= len; t++) {
              const x = tx + d * t * (1 - k * 0.15), y = ty + (t * t) / (10 + k * 3) - t * (0.3 - k * 0.12);
              ctx.fillStyle = PAL[3];
              ctx.fillRect(Math.round(x), Math.round(y), 2, 1);
            }
          }
        }
        rect(ctx, tx - 1, ty - 1, 3, 2, PAL[3]);
      });
      return true;
    }
    case "sunrise": {
      // dithered glow above the horizon, sun half-risen, a big volcano-like mountain and hills
      dither(ctx, H - 16, H, PAL[1]);
      const sx = Math.round(mod(112 - s * 0.03, W + 40) - 20);
      dome(ctx, sx, H - 6, 14, 14, PAL[1]);
      dome(ctx, sx, H - 6, 12, 12, PAL[0]);
      dome(ctx, sx, H - 6, 8, 8, PAL[1]);
      for (let i = 0; i < 6; i++) rect(ctx, sx - 24 + i * 9, H - 22 - (i % 2) * 3, 5, 1, PAL[1]); // rays
      const mx = Math.round(mod(52 - s * 0.12, W + 120) - 60);
      triangle(ctx, mx, H, 48, 30, PAL[2]);
      for (let dy = 0; dy < 10; dy++) {
        const half = Math.round((48 * (dy + 1)) / 30);
        rect(ctx, mx - half + ((dy + 1) & 1), H - 30 + dy, half * 2 - (((dy + 1) & 1) * 2), 1, PAL[0]);
        if (dy > 5) rect(ctx, mx - half + 2, H - 30 + dy, half * 2 - 4, 1, PAL[2]);
      }
      cells(s * 0.7, 26, 8, (cx, h) => dome(ctx, cx, H, 15, 5 + (h % 4), PAL[3]));
      return true;
    }
    case "forest": {
      clouds(ctx, s);
      const pine = (cx: number, h: number, c: string) => {
        const tiers = Math.max(2, Math.round(h / 6));
        rect(ctx, cx, H - 2, 2, 2, c);
        for (let i = 0; i < tiers; i++) {
          const th = Math.round(h / tiers) + 3, w = Math.round(2 + ((tiers - i) * 6) / tiers);
          triangle(ctx, cx, H - 2 - (i * h) / tiers, w + 1, th, c);
        }
      };
      cells(s * 0.55, 8, 10, (cx, h) => pine(cx, 12 + (h % 8), PAL[2]));
      cells(s, 11, 11, (cx, h) => pine(cx, 14 + (h % 12), PAL[3]));
      return true;
    }
    case "hills": {
      clouds(ctx, s);
      cells(s * 0.4, 52, 12, (cx, h) => {
        dome(ctx, cx, H, 34, 14 + (h % 6), PAL[1]);
      });
      cells(s * 0.85, 22, 13, (cx, h) => dome(ctx, cx, H, 14, 6 + (h % 4), PAL[2]));
      // a few round trees on the near hill
      cells(s, 15, 14, (cx, h) => {
        if (h % 2) return;
        dome(ctx, cx, H, 5, 7, PAL[3]);
      });
      return true;
    }
    case "desert": {
      // big pale sun and flat-topped mesas, cacti close by
      const sx = Math.round(mod(34 - s * 0.04, W + 40) - 20);
      dome(ctx, sx, 22, 11, 11, PAL[1]);
      dome(ctx, sx, 22, 11, 11, PAL[1]);
      rect(ctx, sx - 11, 22, 22, 1, PAL[1]);
      cells(s * 0.45, 58, 15, (cx, h) => {
        const w = 24 + (h % 12), hh = 12 + ((h >> 4) % 8);
        ctx.fillStyle = PAL[2];
        for (let dy = 0; dy < hh; dy++) {
          const half = Math.round(w / 2 + (dy * 3) / 2);
          ctx.fillRect(cx - half, H - hh + dy, half * 2, 1);
        }
        for (let dy = 3; dy < hh; dy += 4) rect(ctx, cx - w / 2 - dy * 0.8, H - hh + dy, w + dy * 1.6, 1, PAL[3]); // strata
      });
      cells(s * 0.85, 40, 16, (cx, h) => dome(ctx, cx, H, 20, 5 + (h % 3), PAL[1])); // dunes
      cells(s, 30, 17, (cx, h) => {
        if (h % 2) return;
        const ht = 9 + (h % 5);
        rect(ctx, cx, H - ht, 2, ht, PAL[3]);
        rect(ctx, cx - 3, H - ht + 3, 2, 4, PAL[3]);
        rect(ctx, cx - 3, H - ht + 6, 4, 1, PAL[3]);
        rect(ctx, cx + 4, H - ht + 2, 2, 5, PAL[3]);
        rect(ctx, cx + 2, H - ht + 6, 4, 1, PAL[3]);
      });
      return true;
    }
    case "seaside": {
      // open sea with sailing boats, a slow cargo ship and gulls; palms and foam on the shore
      clouds(ctx, s);
      const sy = H - 11;
      rect(ctx, 0, sy, W, 11, PAL[1]);
      rect(ctx, 0, sy, W, 1, PAL[2]);
      cells(s * 0.5, 17, 70, (cx, h) => rect(ctx, cx + ((h >> 3) % 9), sy + 2 + (h % 6), 3, 1, PAL[0])); // glitter
      const shx = Math.round(mod(t * 0.05 + 90 - s * 0.1, W + 100)) - 50; // cargo ship, far out
      rect(ctx, shx, sy - 3, 26, 3, PAL[3]);
      rect(ctx, shx + 3, sy - 6, 5, 3, PAL[2]);
      rect(ctx, shx + 9, sy - 6, 5, 3, PAL[3]);
      rect(ctx, shx + 19, sy - 7, 4, 4, PAL[2]);
      rect(ctx, shx + 20, sy - 9, 2, 2, PAL[3]);
      cells(s * 0.35, 64, 71, (cx, h) => {
        if (h % 3 === 0) return;
        const bx = cx + (h % 20), by = sy + 4 + ((h >> 5) % 3) + (((t >> 4) + h) & 1); // bobbing on the swell
        rect(ctx, bx, by, 9, 2, PAL[3]);
        rect(ctx, bx + 4, by - 9, 1, 9, PAL[3]);
        triangle(ctx, bx + 7, by - 1, 3, 8, PAL[0]);
        triangle(ctx, bx + 2, by - 1, 2, 6, PAL[0]);
      });
      rect(ctx, 0, H - 3, W, 3, PAL[0]); // beach
      cells(s, 9, 72, (cx, h) => { if (h % 3) rect(ctx, cx, H - 4, 5, 1, PAL[0]); }); // foam
      cells(s * 0.9, 41, 73, (cx, h) => {
        if (h % 4 === 0) return;
        const ht = 9 + (h % 4);
        rect(ctx, cx, H - 3 - ht, 1, ht, PAL[3]);
        rect(ctx, cx - 4, H - 3 - ht, 9, 1, PAL[3]);
        rect(ctx, cx - 3, H - 4 - ht, 7, 1, PAL[3]);
        rect(ctx, cx - 5, H - 2 - ht, 2, 1, PAL[3]);
        rect(ctx, cx + 4, H - 2 - ht, 2, 1, PAL[3]);
      });
      for (let i = 0; i < 4; i++) {
        const gx = Math.round(mod(t * (0.35 + i * 0.07) + i * 53 - s * 0.12, W + 30)) - 15;
        gull(ctx, gx, 7 + i * 5 + Math.round(Math.sin((t + i * 40) / 25) * 2), ((t >> 3) + i) % 2 === 0);
      }
      return true;
    }
    case "airport": {
      // control tower, hangars and parked jets on the horizon; a jet and a helicopter cross the sky
      clouds(ctx, s);
      cells(s * 0.4, 70, 80, (cx, h) => { // far hangars
        dome(ctx, cx, H - 2, 14 + (h % 6), 7, PAL[1]);
        rect(ctx, cx - 5, H - 5, 10, 3, PAL[2]);
      });
      cells(s * 0.6, 170, 81, (cx) => { // control tower
        rect(ctx, cx, H - 24, 3, 22, PAL[2]);
        rect(ctx, cx - 4, H - 30, 11, 6, PAL[3]);
        rect(ctx, cx - 3, H - 29, 9, 2, PAL[0]);
        rect(ctx, cx - 5, H - 31, 13, 1, PAL[2]);
        rect(ctx, cx + 1, H - 38, 1, 7, PAL[3]);
        rect(ctx, cx - 1, H - 36, 5, 1, PAL[3]);
        if ((t >> 5) & 1) rect(ctx, cx, H - 39, 3, 1, PAL[2]); // beacon blinks
      });
      cells(s * 0.9, 96, 82, (cx, h) => { // parked jets (side view)
        if (h % 4 === 0) return;
        rect(ctx, cx, H - 8, 20, 3, PAL[3]);
        rect(ctx, cx + 17, H - 7, 4, 2, PAL[3]);
        rect(ctx, cx, H - 13, 3, 5, PAL[3]);
        rect(ctx, cx + 7, H - 5, 8, 1, PAL[2]);
        rect(ctx, cx + 9, H - 4, 3, 2, PAL[3]);
        for (let k = 0; k < 5; k++) rect(ctx, cx + 5 + k * 2, H - 7, 1, 1, PAL[1]);
      });
      rect(ctx, 0, H - 2, W, 2, PAL[2]); // apron edge
      cells(s, 14, 83, (cx) => rect(ctx, cx, H - 1, 6, 1, PAL[0])); // taxiway markings
      const jx = Math.round(mod(t * 0.7 + 20 - s * 0.05, W + 90)) - 45; // jet climbing out
      const jy = 14 - Math.round((jx + 45) / 18);
      for (let k = 1; k < 6; k++) rect(ctx, jx - 4 - k * 5, jy + 1 + (k >> 2), 3, 1, PAL[1]); // contrail
      rect(ctx, jx, jy, 15, 2, PAL[3]);
      rect(ctx, jx + 15, jy, 2, 1, PAL[3]);
      rect(ctx, jx, jy - 3, 2, 3, PAL[3]);
      rect(ctx, jx + 5, jy + 2, 6, 1, PAL[2]);
      const hx = Math.round(mod(t * 0.3 + 60 - s * 0.04, W + 90)) - 45; // helicopter, nose first (flies to the right)
      const hy = 26 + Math.round(Math.sin(t / 30) * 1.5);
      rect(ctx, hx, hy, 7, 3, PAL[3]);
      rect(ctx, hx - 1, hy + 1, 1, 1, PAL[3]);
      rect(ctx, hx + 7, hy + 1, 1, 1, PAL[3]);
      rect(ctx, hx + 5, hy + 1, 2, 1, PAL[0]);
      rect(ctx, hx - 7, hy, 7, 1, PAL[3]);
      rect(ctx, hx - 7, hy - 2, 1, 3, PAL[3]);
      rect(ctx, hx + 1, hy + 4, 6, 1, PAL[3]);
      if ((t >> 1) & 1) rect(ctx, hx - 4, hy - 2, 13, 1, PAL[3]); else rect(ctx, hx, hy - 2, 7, 1, PAL[3]); // rotor
      rect(ctx, hx + 3, hy - 1, 1, 1, PAL[3]);
      return true;
    }
    case "quebec": {
      // Quebec City: Laurentians and Ile d'Orleans across the St Lawrence, then the cliff with the Chateau Frontenac
      // (copper roofs, turrets, central tower) and the Hotel Concorde tower with its revolving restaurant
      clouds(ctx, s);
      cells(s * 0.12, 96, 100, (cx, h) => triangle(ctx, cx, H - 3, 34 + (h % 12), 9 + (h % 8), PAL[1])); // Laurentians
      cells(s * 0.25, 120, 101, (cx, h) => { // Ile d'Orleans: long, low, with a few farms and a steeple
        dome(ctx, cx, H - 3, 46, 4, PAL[2]);
        rect(ctx, cx - 20 + (h % 9), H - 6, 1, 2, PAL[3]);
        rect(ctx, cx + 8 + (h % 7), H - 6, 1, 2, PAL[3]);
        rect(ctx, cx + 24, H - 8, 1, 4, PAL[3]);
      });
      dither(ctx, H - 3, H, PAL[1]); // river
      // the landmarks cross the screen one after the other, always at least one on screen: the bridge far behind, then the cliff with the chateau and the Concorde
      landmark(s, 200, 1.3, 75, (lx) => sprite("laporte", ctx, lx)); // Pierre-Laporte Bridge: suspension bridge (the old Quebec Bridge sprite, assets/scenery/bridge.png, is no longer drawn) // Quebec Bridge: cantilever steel truss on two piers with a suspended centre span
      landmark(s, 88, 2.0, 95, (mid) => sprite("chateau", ctx, mid)); // chateau
      const bx = Math.round(mod(t * 0.25 + 40 - s * 0.1, W + 60)) - 30; // the Quebec-Levis ferry
      rect(ctx, bx, H - 2, 14, 2, PAL[3]);
      rect(ctx, bx + 3, H - 4, 7, 2, PAL[2]);
      rect(ctx, bx + 6, H - 6, 1, 2, PAL[3]);
      for (let i = 0; i < 2; i++) gull(ctx, Math.round(mod(t * (0.35 + i * 0.1) + i * 70 - s * 0.05, W + 40)) - 20, 9 + i * 6, ((t >> 3) + i) % 2 === 0);
      return true;
    }
    case "manx": {
      // Isle of Man TT: Snaefell with its summit mast, heather moor, whitewashed cottages, dry-stone walls and the Laxey Wheel
      clouds(ctx, s);
      cells(s * 0.1, 170, 120, (cx, h) => { // Snaefell: broad and rounded, mast on top
        dome(ctx, cx, H - 3, 80, 24 + (h % 4), PAL[1]);
        rect(ctx, cx + 6, H - 31 - (h % 4), 1, 6, PAL[3]);
        rect(ctx, cx + 4, H - 28 - (h % 4), 5, 1, PAL[3]);
      });
      cells(s * 0.3, 64, 121, (cx, h) => dome(ctx, cx, H - 1, 38, 8 + (h % 5), PAL[2])); // moor
      // the Laxey Wheel: stone tower with the great waterwheel, its spokes turning slowly
      landmark(s, 130, 1.6, 26, (wx) => {
        const cy = H - 19, r = 15;
        rect(ctx, wx - 3, cy, 7, 19, PAL[3]); // tower
        rect(ctx, wx - 1, cy + 3, 3, 2, PAL[0]);
        for (let a = 0; a < 64; a++) rect(ctx, wx + Math.cos((a / 64) * 6.2832) * r, cy + Math.sin((a / 64) * 6.2832) * r, 1, 1, PAL[3]);
        for (let a = 0; a < 64; a++) rect(ctx, wx + Math.cos((a / 64) * 6.2832) * (r - 2), cy + Math.sin((a / 64) * 6.2832) * (r - 2), 1, 1, PAL[2]);
        for (let k = 0; k < 8; k++) {
          const ang = (k / 8) * 6.2832 + t * 0.012;
          line(ctx, wx, cy, wx + Math.round(Math.cos(ang) * (r - 1)), cy + Math.round(Math.sin(ang) * (r - 1)), PAL[3]);
        }
        rect(ctx, wx - 1, cy - 1, 3, 3, PAL[3]); // hub
        for (let i = 0; i < 6; i++) rect(ctx, wx - 18 + i * 3, H - 3 - i, 3, 1, PAL[3]); // stair to the viewing gallery
      });
      cells(s * 0.75, 74, 122, (cx, h) => { // whitewashed cottage with a chimney, slate roof
        const w = 11 + (h % 4);
        rect(ctx, cx - w / 2 - 1, H - 8, w + 2, 8, PAL[3]);
        rect(ctx, cx - w / 2, H - 7, w, 7, PAL[0]);
        triangle(ctx, cx, H - 8, w / 2 + 2, 4, PAL[3]);
        rect(ctx, cx + w / 4, H - 14, 2, 4, PAL[3]);
        rect(ctx, cx - 1, H - 4, 2, 4, PAL[2]);
        rect(ctx, cx - w / 2 + 2, H - 5, 2, 2, PAL[2]);
      });
      // dry-stone wall along the roadside: two rows of irregular stones
      cells(s * 1.0, 5, 123, (cx, h) => {
        rect(ctx, cx, H - 3, 3 + (h % 2), 2, PAL[3]);
        rect(ctx, cx + 2 + (h % 3), H - 1, 3, 1, PAL[3]);
        rect(ctx, cx, H - 1, 1, 1, PAL[2]);
      });
      for (let i = 0; i < 2; i++) gull(ctx, Math.round(mod(t * (0.3 + i * 0.12) + i * 90 - s * 0.05, W + 40)) - 20, 8 + i * 7, ((t >> 3) + i) % 2 === 0);
      return true;
    }
    case "karting": {
      // kart circuit: low hills and a tree line behind, the pit building with its timing tower, floodlight pylons, flags and walls of stacked tyres
      clouds(ctx, s);
      cells(s * 0.12, 90, 130, (cx, h) => dome(ctx, cx, H - 4, 46, 9 + (h % 6), PAL[1]));
      cells(s * 0.3, 14, 131, (cx, h) => dome(ctx, cx, H - 2, 7, 5 + (h % 4), PAL[2])); // tree line
      landmark(s, 110, 1.2, 46, (bx) => { // pit building: flat roof, windows, timing tower with a flag
        rect(ctx, bx - 40, H - 14, 80, 14, PAL[3]);
        rect(ctx, bx - 40, H - 16, 80, 2, PAL[2]);
        for (let i = 0; i < 9; i++) rect(ctx, bx - 36 + i * 8, H - 10, 5, 4, PAL[0]);
        rect(ctx, bx + 24, H - 26, 12, 10, PAL[3]); // tower
        rect(ctx, bx + 26, H - 24, 8, 3, PAL[0]);
        rect(ctx, bx + 30, H - 34, 1, 8, PAL[3]);
        for (let i = 0; i < 4; i++) for (let j = 0; j < 2; j++) rect(ctx, bx + 31 + i * 2, H - 34 + j * 2, 2, 2, (i + j) % 2 ? PAL[0] : PAL[3]); // chequered flag
      });
      cells(s * 0.55, 96, 132, (cx) => { // floodlight pylon
        rect(ctx, cx, H - 22, 1, 22, PAL[3]);
        rect(ctx, cx - 4, H - 24, 9, 2, PAL[3]);
        for (let i = 0; i < 4; i++) rect(ctx, cx - 3 + i * 2, H - 22, 1, 1, PAL[0]);
      });
      cells(s * 0.9, 7, 133, (cx, h) => { // stacked tyres along the track: two rows, alternating
        rect(ctx, cx, H - 5, 5, 2, PAL[3]);
        rect(ctx, cx + 3, H - 3, 5, 3, PAL[3]);
        rect(ctx, cx + 1, H - 5 + (h & 1), 1, 1, PAL[0]);
        rect(ctx, cx + 5, H - 2, 1, 1, PAL[0]);
      });
      return true;
    }
    case "montreal": {
      // Montreal: Mount Royal with its cross, the downtown towers and the Olympic Stadium with its leaning tower
      clouds(ctx, s);
      // Mount Royal stays on the horizon all along; the downtown and the stadium cross the screen at their own speeds (always one of each on screen)
      landmark(s, 60, 0.9, 100, (mx) => sprite("mount", ctx, mx)); // mount
      landmark(s, 150, 1.4, 55, (cx) => sprite("laronde", ctx, cx)); // La Ronde across the river: behind the downtown
      landmark(s, 118, 1.8, 62, (dx) => { sprite("downtown", ctx, dx); if ((t >> 5) & 1) rect(ctx, dx - 11, H - 41, 1, 1, PAL[3]); }); // blinking beacon stays in code
      landmark(s, 30, 2.6, 62, (cx) => sprite("stadium", ctx, cx)); // Olympic Stadium: low bowl, ring roof and the inclined tower held by cables
      const bx = Math.round(mod(t * 0.2 + 30 - s * 0.1, W + 60)) - 30; // a boat on the St Lawrence
      rect(ctx, bx, H - 2, 12, 1, PAL[3]);
      rect(ctx, bx + 3, H - 4, 5, 2, PAL[2]);
      for (let i = 0; i < 2; i++) gull(ctx, Math.round(mod(t * (0.3 + i * 0.12) + i * 80 - s * 0.05, W + 40)) - 20, 8 + i * 7, ((t >> 3) + i) % 2 === 0);
      return true;
    }
    case "paris": {
      // Paris: Sacre-Coeur on its hill, the Eiffel Tower, Notre-Dame and the Arc de Triomphe over a low Haussmann skyline (zinc mansard roofs), the Seine and its tourist boats
      clouds(ctx, s);
      const haussmann = (cx: number, w: number, hh: number, body: string, roof: string, win: string, h: number) => {
        rect(ctx, cx, H - hh, w, hh, body);
        rect(ctx, cx - 1, H - hh - 3, w + 2, 3, roof); // mansard
        rect(ctx, cx, H - hh - 4, w, 1, roof);
        if (h % 3 === 0) rect(ctx, cx + (h % 5) + 1, H - hh - 7, 2, 3, roof); // chimney
        for (let y = H - hh + 2; y < H - 1; y += 3) for (let x = cx + 1; x < cx + w - 1; x += 3) rect(ctx, x, y, 1, 2, win);
      };
      cells(s * 0.4, 26, 110, (cx, h) => haussmann(cx, 14 + (h % 7), 6 + ((h >> 3) % 4), PAL[1], PAL[2], PAL[2], h)); // far skyline
      landmark(s, 100, 0.5, 40, (x) => sprite("sacre", ctx, x));
      landmark(s, 210, 0.9, 40, (x) => sprite("eiffel", ctx, x));
      cells(s * 1.3, 34, 111, (cx, h) => haussmann(cx, 16 + (h % 8), 5 + ((h >> 3) % 4), PAL[2], PAL[3], PAL[1], h + 1)); // near rows
      dither(ctx, H - 3, H, PAL[1]); // the Seine
      landmark(s, 60, 1.8, 50, (x) => sprite("notredame", ctx, x));
      landmark(s, 250, 2.6, 50, (x) => sprite("arc", ctx, x));
      const bx = Math.round(mod(t * 0.2 + 70 - s * 0.1, W + 60)) - 30; // a bateau-mouche on the Seine
      rect(ctx, bx, H - 2, 14, 2, PAL[3]);
      rect(ctx, bx + 2, H - 4, 10, 2, PAL[1]);
      for (let i = 0; i < 3; i++) gull(ctx, Math.round(mod(t * (0.3 + i * 0.1) + i * 60 - s * 0.05, W + 40)) - 20, 7 + i * 6, ((t >> 3) + i) % 2 === 0);
      return true;
    }
    case "rome": {
      // Rome: Saint Peter's far away, the Pantheon, the Colosseum, the Vittoriano and Trajan's Column over ochre roofs and umbrella pines
      clouds(ctx, s);
      const roofs = (cx: number, w: number, hh: number, body: string, tile: string, h: number) => {
        rect(ctx, cx, H - hh, w, hh, body);
        triangle(ctx, cx + (w >> 1), H - hh, (w >> 1) + 1, 3, tile); // low terracotta roof
        if (h % 5 === 0) { rect(ctx, cx + w - 4, H - hh - 9, 3, 9, body); rect(ctx, cx + w - 4, H - hh - 10, 3, 1, tile); rect(ctx, cx + w - 3, H - hh - 7, 1, 2, tile); } // campanile
        for (let y = H - hh + 2; y < H - 1; y += 3) for (let x = cx + 1; x < cx + w - 1; x += 3) rect(ctx, x, y, 1, 2, tile === PAL[3] ? PAL[1] : PAL[3]);
      };
      cells(s * 0.4, 28, 120, (cx, h) => roofs(cx, 14 + (h % 8), 5 + ((h >> 3) % 4), PAL[1], PAL[2], h)); // far roofs
      landmark(s, 90, 0.5, 34, (x) => sprite("peters", ctx, x));
      cells(s * 0.9, 61, 121, (cx, h) => { // umbrella pines
        if (h % 3 === 0) return;
        rect(ctx, cx, H - 9, 1, 9, PAL[3]);
        dome(ctx, cx, H - 8, 7, 3, PAL[2]);
        rect(ctx, cx - 6, H - 9, 13, 1, PAL[2]);
      });
      landmark(s, 215, 1.4, 20, (x) => sprite("pantheon", ctx, x));
      landmark(s, 120, 1.9, 8, (x) => sprite("trajan", ctx, x));
      cells(s * 1.4, 36, 122, (cx, h) => roofs(cx, 16 + (h % 6), 4 + ((h >> 3) % 3), PAL[2], PAL[3], h + 3)); // near roofs
      landmark(s, 40, 1.7, 34, (x) => sprite("colisee", ctx, x));
      landmark(s, 270, 2.5, 36, (x) => sprite("vittoriano", ctx, x));
      for (let i = 0; i < 3; i++) gull(ctx, Math.round(mod(t * (0.3 + i * 0.1) + i * 60 - s * 0.05, W + 40)) - 20, 7 + i * 6, ((t >> 3) + i) % 2 === 0);
      return true;
    }
    case "toronto": {
      // Toronto: the downtown skyline and the CN Tower over Lake Ontario, the Rogers Centre dome, sailboats and gulls
      clouds(ctx, s);
      const block = (cx: number, w: number, hh: number, c: string, h: number) => {
        rect(ctx, cx, H - hh, w, hh, c);
        for (let y = H - hh + 2; y < H - 1; y += 3) for (let x = cx + 1; x < cx + w - 1; x += 2) if ((hash(x * 13 + y * 5 + h) & 3) === 0) rect(ctx, x, y, 1, 1, c === PAL[3] ? PAL[1] : PAL[3]);
      };
      cells(s * 0.4, 18, 130, (cx, h) => block(cx, 8 + (h % 6), 6 + ((h >> 3) % 9), PAL[1], h)); // far towers
      landmark(s, 110, 0.7, 56, (x) => sprite("torontoskyline", ctx, x));
      landmark(s, 195, 1.0, 10, (x) => sprite("cntower", ctx, x));
      cells(s * 1.3, 30, 131, (cx, h) => block(cx, 12 + (h % 7), 4 + ((h >> 3) % 5), PAL[2], h)); // low-rise
      dither(ctx, H - 3, H, PAL[1]); // the lake
      landmark(s, 40, 1.8, 32, (x) => sprite("rogers", ctx, x));
      const sx = Math.round(mod(t * 0.15 + 90 - s * 0.1, W + 40)) - 20; // a sailboat
      rect(ctx, sx, H - 2, 9, 1, PAL[3]); triangle(ctx, sx + 4, H - 3, 3, 6, PAL[0]); rect(ctx, sx + 4, H - 10, 1, 7, PAL[3]);
      for (let i = 0; i < 3; i++) gull(ctx, Math.round(mod(t * (0.3 + i * 0.1) + i * 60 - s * 0.05, W + 40)) - 20, 7 + i * 6, ((t >> 3) + i) % 2 === 0);
      return true;
    }
    case "rio": {
      // Rio de Janeiro: Corcovado with Christ the Redeemer arms spread, Sugarloaf with its cable car, hillsides of stacked houses, Copacabana palms and the bay
      clouds(ctx, s);
      cells(s * 0.12, 120, 140, (cx, h) => dome(ctx, cx, H - 2, 70, 10 + (h % 6), PAL[1])); // far green hills
      landmark(s, 70, 0.5, 44, (cx) => { // Corcovado: a steep rounded granite peak, the statue on its top
        dome(ctx, cx, H - 1, 34, 22, PAL[2]);
        dome(ctx, cx, H - 1, 18, 25, PAL[2]);
        rect(ctx, cx - 17, H - 6, 34, 5, PAL[2]);
        const top = H - 26; // summit
        rect(ctx, cx - 3, top - 1, 7, 2, PAL[3]); // pedestal
        rect(ctx, cx - 1, top - 9, 3, 8, PAL[3]); // robe
        rect(ctx, cx - 2, top - 4, 5, 3, PAL[3]); // flared robe
        rect(ctx, cx - 7, top - 8, 15, 2, PAL[3]); // arms spread wide
        rect(ctx, cx - 1, top - 12, 3, 3, PAL[3]); // head
        rect(ctx, cx, top - 7, 1, 3, PAL[0]); // the light on the chest
      });
      landmark(s, 205, 0.9, 38, (cx) => { // Sugarloaf with the second hill and the cable car between them
        dome(ctx, cx, H - 1, 18, 24, PAL[2]);
        dome(ctx, cx + 36, H - 1, 12, 13, PAL[2]);
        for (let x = cx + 2; x < cx + 34; x++) rect(ctx, x, H - 24 + Math.floor(((x - cx - 2) * 11) / 32), 1, 1, PAL[3]); // the cable
        const gx = cx + 4 + ((t >> 1) % 26); // gondola
        rect(ctx, gx, H - 22 + Math.floor(((gx - cx - 2) * 11) / 32), 3, 2, PAL[3]);
      });
      const hillside = (cx: number, w: number, hh: number, h: number) => { // a hill covered with rows of small houses, favela style
        dome(ctx, cx, H - 1, w, hh, PAL[1]);
        for (let k = 3; k < hh - 2; k += 3) {
          const half = w * Math.sqrt(1 - (k / hh) ** 2);
          for (let x = cx - half + 3; x < cx + half - 4; x += 4) {
            const r = hash(h + k * 31 + Math.floor(x) * 7);
            if (r % 4 === 0) continue;
            rect(ctx, x + (r % 2), H - 1 - k, 3, 2, r % 3 === 0 ? PAL[0] : r % 3 === 1 ? PAL[3] : PAL[2]);
          }
        }
      };
      cells(s * 0.5, 96, 141, (cx, h) => hillside(cx, 36 + (h % 8), 10 + (h % 6), h));
      cells(s * 1.1, 44, 142, (cx, h) => { // apartment blocks along the avenue
        const w = 9 + (h % 5), hh = 7 + ((h >> 3) % 8);
        rect(ctx, cx, H - hh, w, hh, PAL[2]);
        for (let y = H - hh + 2; y < H - 1; y += 3) for (let x = cx + 1; x < cx + w - 1; x += 3) rect(ctx, x, y, 1, 1, PAL[0]);
      });
      cells(s * 1.7, 38, 143, (cx, h) => { // palm trees of Copacabana
        const ph = 9 + (h % 4);
        rect(ctx, cx, H - ph, 1, ph, PAL[3]);
        for (const [dx, dy] of [[-4, 1], [-3, 0], [-2, -1], [2, -1], [3, 0], [4, 1], [0, -2], [-1, -1], [1, -1]]) rect(ctx, cx + dx, H - ph + dy, 2, 1, PAL[3]);
      });
      dither(ctx, H - 3, H, PAL[1]); // the bay
      const bx = Math.round(mod(t * 0.2 + 40 - s * 0.1, W + 60)) - 30; // a sailboat
      rect(ctx, bx, H - 2, 9, 1, PAL[3]); triangle(ctx, bx + 4, H - 3, 3, 6, PAL[0]); rect(ctx, bx + 4, H - 10, 1, 7, PAL[3]);
      for (let i = 0; i < 3; i++) gull(ctx, Math.round(mod(t * (0.3 + i * 0.1) + i * 60 - s * 0.05, W + 40)) - 20, 7 + i * 6, ((t >> 3) + i) % 2 === 0);
      return true;
    }
    case "paques": {
      // Easter Island (Rapa Nui): grassy volcanic hills, the crater of Rano Raraku, rows of moai on their ahu platforms, lone statues, wild horses and the Pacific
      clouds(ctx, s);
      const moai = (cx: number, hh: number, c: string, shade: string) => { // a statue seen from the front: long head, heavy brow, long nose, thin mouth, small torso
        const w = 6 + (hh >> 3);
        rect(ctx, cx - (w >> 1), H - hh, w, hh, c); // the body is one big block
        rect(ctx, cx - (w >> 1) - 1, H - hh + 1, 1, hh - 1, shade); // left shade
        rect(ctx, cx - (w >> 1), H - hh, w, 1, shade); // top of the head
        rect(ctx, cx - (w >> 1), H - hh + Math.floor(hh * 0.28), w, 1, shade); // brow ridge
        rect(ctx, cx - 1, H - hh + Math.floor(hh * 0.3), 1, Math.floor(hh * 0.22), shade); // nose
        rect(ctx, cx - 1, H - hh + Math.floor(hh * 0.56), 3, 1, shade); // mouth
        rect(ctx, cx - (w >> 1), H - hh + Math.floor(hh * 0.62), w, 1, shade); // chin line: the torso starts
      };
      cells(s * 0.12, 130, 150, (cx, h) => dome(ctx, cx, H - 2, 75, 9 + (h % 5), PAL[1])); // far green hills
      landmark(s, 60, 0.5, 60, (cx) => { // Rano Raraku: the quarry volcano with unfinished statues on its slope
        dome(ctx, cx, H - 1, 56, 22, PAL[2]);
        dome(ctx, cx + 10, H - 14, 24, 8, PAL[1]); // the crater lake
        for (let i = 0; i < 7; i++) moai(cx - 40 + i * 12, 5 + ((i * 5) % 4), PAL[3], PAL[2]);
      });
      landmark(s, 200, 1.1, 70, (cx) => { // Ahu Tongariki: fifteen moai on one platform (seven in view), backs to the sea
        rect(ctx, cx - 62, H - 4, 124, 4, PAL[3]);
        rect(ctx, cx - 62, H - 4, 124, 1, PAL[2]);
        for (let i = 0; i < 7; i++) moai(cx - 54 + i * 18, 20 + ((i * 7) % 5), PAL[2], PAL[3]);
      });
      cells(s * 0.8, 150, 151, (cx, h) => { // wild horses grazing
        const hx = cx + 40, hy = H - 3;
        rect(ctx, hx, hy - 4, 7, 3, PAL[3]); rect(ctx, hx + 6, hy - 6, 2, 3, PAL[3]); // body, neck
        rect(ctx, hx, hy - 1, 1, 3, PAL[3]); rect(ctx, hx + 5, hy - 1, 1, 3, PAL[3]); // legs
        if (h & 1) rect(ctx, hx - 1, hy - 5, 1, 3, PAL[3]); // tail
      });
      cells(s * 1.6, 90, 152, (cx, h) => moai(cx, 9 + (h % 6), PAL[3], PAL[2])); // lone statues near the road
      cells(s * 1.0, 9, 153, (cx, h) => rect(ctx, cx, H - 1 - (h % 2), 2 + (h % 3), 1, PAL[1])); // volcanic stones in the grass
      dither(ctx, H - 2, H, PAL[1]); // the ocean beyond the cliffs
      const sx = Math.round(mod(120 - s * 0.03, W + 40) - 20); // low sun
      dome(ctx, sx, H - 14, 6, 6, PAL[0]);
      for (let i = 0; i < 3; i++) gull(ctx, Math.round(mod(t * (0.3 + i * 0.1) + i * 60 - s * 0.05, W + 40)) - 20, 7 + i * 6, ((t >> 3) + i) % 2 === 0);
      return true;
    }
    case "hawaii": {
      // Hawaii: a broad shield volcano with its plume and lava streams, the crater ridge of Diamond Head, black lava rock, palms, surfers on the swell
      clouds(ctx, s);
      landmark(s, 80, 0.4, 80, (cx) => { // Mauna Loa: huge and gently sloped, smoke from the summit, glowing lava runs down the flank
        dome(ctx, cx, H - 2, 78, 22, PAL[2]);
        rect(ctx, cx - 7, H - 25, 14, 2, PAL[3]); // summit caldera
        for (let i = 0; i < 4; i++) rect(ctx, cx - 2 + Math.round(Math.sin((t + i * 11) / 18) * 3) + (i >> 1), H - 29 - i * 3, 5 - (i >> 1), 3, i % 2 ? PAL[1] : PAL[0]); // smoke
        for (let k = 0; k < 18; k++) { // lava stream
          const ly = H - 23 + k, lx = cx + 1 + Math.round(Math.sin(k / 3) * 2) + (k >> 1);
          rect(ctx, lx, ly, 2, 1, (t >> 3) % 2 && k % 5 === 0 ? PAL[0] : PAL[3]);
        }
      });
      landmark(s, 240, 0.8, 50, (cx) => { // Diamond Head: a crater ridge, steep on the sea side
        triangle(ctx, cx - 6, H - 2, 30, 20, PAL[2]);
        rect(ctx, cx + 10, H - 16, 22, 14, PAL[2]);
        for (let i = 0; i < 14; i++) rect(ctx, cx + 24 + (i >> 1), H - 16 + i, 4 - (i >> 2), 1, PAL[2]);
        rect(ctx, cx - 18, H - 8, 40, 1, PAL[3]); // path along the ridge
      });
      cells(s * 0.7, 52, 160, (cx, h) => { // far palms on the shore
        const ph = 7 + (h % 3);
        rect(ctx, cx, H - ph, 1, ph, PAL[3]);
        for (const [dx, dy] of [[-3, 1], [-2, 0], [2, 0], [3, 1], [0, -1], [-1, 0], [1, 0]]) rect(ctx, cx + dx, H - ph + dy, 2, 1, PAL[3]);
      });
      cells(s * 1.2, 11, 161, (cx, h) => rect(ctx, cx, H - 3 - (h % 3), 4 + (h % 4), 3 + (h % 3), PAL[3])); // black lava rocks along the road
      dither(ctx, H - 3, H, PAL[1]); // the sea
      const wx = Math.round(mod(t * 0.25 + 60 - s * 0.08, W + 50)) - 25; // a surfer riding a wave
      rect(ctx, wx - 6, H - 3, 12, 1, PAL[0]); rect(ctx, wx - 5, H - 2, 10, 1, PAL[0]);
      rect(ctx, wx - 2, H - 5, 5, 1, PAL[3]); rect(ctx, wx, H - 8, 1, 3, PAL[3]); rect(ctx, wx, H - 9, 1, 1, PAL[3]); rect(ctx, wx - 1, H - 7, 3, 1, PAL[3]);
      const sx = Math.round(mod(150 - s * 0.03, W + 40) - 20); // sun
      dome(ctx, sx, H - 18, 7, 7, PAL[0]);
      for (let i = 0; i < 3; i++) gull(ctx, Math.round(mod(t * (0.3 + i * 0.1) + i * 60 - s * 0.05, W + 40)) - 20, 7 + i * 6, ((t >> 3) + i) % 2 === 0);
      return true;
    }
    case "tokyo": {
      // Tokyo: Mount Fuji far away, Tokyo Tower and the Skytree over dense skyscrapers with neon signs, a bullet train crossing the screen
      clouds(ctx, s);
      landmark(s, 100, 0.25, 70, (cx) => { // Mount Fuji: a long, flat cone with a snow cap
        triangle(ctx, cx, H - 2, 56, 27, PAL[1]);
        triangle(ctx, cx, H - 16, 15, 11, PAL[0]);
        rect(ctx, cx - 6, H - 8, 12, 1, PAL[0]);
      });
      const tower = (cx: number, hh: number, base: number) => { // a lattice tower with red and white bands, antenna on the top
        for (let y = 0; y < hh; y++) {
          const hw = Math.max(1, Math.round(base * (1 - y / hh) ** 1.4));
          rect(ctx, cx - hw, H - 1 - y, 1, 1, PAL[3]); rect(ctx, cx + hw, H - 1 - y, 1, 1, PAL[3]);
          if (y % 4 === 0) rect(ctx, cx - hw, H - 1 - y, hw * 2 + 1, 1, y % 8 === 0 ? PAL[3] : PAL[2]);
        }
        rect(ctx, cx - 3, H - 1 - Math.round(hh * 0.55), 7, 2, PAL[3]); // observation deck
        rect(ctx, cx, H - 1 - hh - 4, 1, 5, PAL[3]);
      };
      landmark(s, 55, 0.8, 24, (cx) => tower(cx, 38, 8)); // Tokyo Tower
      landmark(s, 215, 1.0, 14, (cx) => { rect(ctx, cx - 1, H - 40, 3, 40, PAL[3]); rect(ctx, cx - 3, H - 24, 7, 2, PAL[3]); rect(ctx, cx - 2, H - 33, 5, 2, PAL[3]); rect(ctx, cx, H - 44, 1, 5, PAL[3]); }); // Skytree
      const tall = (cx: number, w: number, hh: number, body: string, h: number) => {
        rect(ctx, cx, H - hh, w, hh, body);
        for (let y = H - hh + 2; y < H - 1; y += 3) for (let x = cx + 1; x < cx + w - 1; x += 2) if (hash(x * 7 + y * 3 + h) % 3 === 0) rect(ctx, x, y, 1, 1, body === PAL[3] ? PAL[0] : PAL[3]);
      };
      cells(s * 0.45, 16, 170, (cx, h) => tall(cx, 8 + (h % 6), 10 + ((h >> 3) % 15), PAL[1], h)); // far skyscrapers
      cells(s * 1.1, 24, 171, (cx, h) => { // near towers with a neon sign on the side
        tall(cx, 12 + (h % 7), 8 + ((h >> 3) % 11), PAL[2], h);
        if (h % 3 === 0) rect(ctx, cx + 1, H - 12 - ((h >> 4) % 4), 2, 6 + (h % 3), (t >> 4) % 2 ? PAL[0] : PAL[3]);
      });
      // a shinkansen on its viaduct: long white cars with a blue stripe, a row of windows and the long pointed nose, going right
      const tx = Math.round(mod(t + 300, 720) * 2.4) - 90; // one pass about every 12 s, then the viaduct is empty
      rect(ctx, 0, H - 7, W, 2, PAL[3]); // viaduct deck
      for (let x = -((s * 1.1) % 16 | 0); x < W; x += 16) rect(ctx, x, H - 5, 2, 5, PAL[3]); // pillars scroll with the road
      const L = 66;
      rect(ctx, tx, H - 14, L, 7, PAL[0]); // body
      rect(ctx, tx, H - 15, L, 1, PAL[3]); // roof line
      rect(ctx, tx, H - 8, L, 1, PAL[3]); // skirt
      rect(ctx, tx, H - 10, L, 1, PAL[2]); // blue stripe
      for (let c = 0; c < 4; c++) {
        const cx = tx + c * 16 + 2;
        for (let w = 0; w < 4; w++) rect(ctx, cx + w * 3, H - 13, 2, 2, PAL[3]); // windows
        rect(ctx, tx + c * 16 + 15, H - 14, 1, 7, PAL[2]); // gap between cars
      }
      for (let i = 0; i < 10; i++) { // the nose: a long slope down to a rounded tip
        const top = H - 14 + Math.floor(i * 0.7);
        rect(ctx, tx + L + i, top, 1, H - 7 - top, PAL[0]);
        rect(ctx, tx + L + i, top - 1, 1, 1, PAL[3]);
      }
      rect(ctx, tx + L + 3, H - 12, 3, 2, PAL[3]); // cab window
      rect(ctx, tx + 20, H - 17, 3, 2, PAL[3]); rect(ctx, tx + 44, H - 17, 3, 2, PAL[3]); // pantographs
      for (let i = 0; i < 3; i++) gull(ctx, Math.round(mod(t * (0.3 + i * 0.1) + i * 60 - s * 0.05, W + 40)) - 20, 7 + i * 6, ((t >> 3) + i) % 2 === 0);
      return true;
    }
    case "savanna": {
      // low orange sun, a far snow-capped mountain, acacia silhouettes and herds of giraffes and elephants
      const sx = Math.round(mod(120 - s * 0.03, W + 40) - 20);
      dome(ctx, sx, H - 6, 12, 12, PAL[1]);
      cells(s * 0.2, 150, 90, (cx) => { // far volcano
        triangle(ctx, cx, H, 34, 20, PAL[1]);
        triangle(ctx, cx, H - 11, 11, 9, PAL[0]);
      });
      cells(s * 0.5, 44, 91, (cx, h) => { // distant acacias
        if (h % 3 === 0) return;
        const ht = 8 + (h % 3);
        rect(ctx, cx, H - ht, 1, ht, PAL[2]);
        rect(ctx, cx - 6, H - ht - 1, 13, 2, PAL[2]);
        rect(ctx, cx - 4, H - ht - 2, 9, 1, PAL[2]);
      });
      cells(s * 0.8, 112, 92, (cx, h) => { // herds, walking
        const step = (t >> 4) & 1;
        if (h % 2 === 0) { // giraffe
          rect(ctx, cx, H - 7, 8, 3, PAL[3]);
          rect(ctx, cx + 6, H - 14, 2, 8, PAL[3]);
          rect(ctx, cx + 6, H - 15, 4, 2, PAL[3]);
          rect(ctx, cx + 1, H - 4, 1, 4, PAL[3]);
          rect(ctx, cx + 3 + step, H - 4, 1, 4, PAL[3]);
          rect(ctx, cx + 6, H - 4, 1, 4, PAL[3]);
        } else { // elephant
          rect(ctx, cx, H - 9, 12, 6, PAL[2]);
          rect(ctx, cx + 11, H - 11, 5, 6, PAL[2]);
          rect(ctx, cx + 15, H - 8, 2, 6, PAL[2]);
          rect(ctx, cx + 1, H - 4, 2, 4, PAL[3]);
          rect(ctx, cx + 4 + step, H - 4, 2, 4, PAL[3]);
          rect(ctx, cx + 8, H - 4, 2, 4, PAL[3]);
        }
      });
      cells(s, 31, 93, (cx, h) => { if (h % 3) rect(ctx, cx, H - 2, 1, 2, PAL[2]); }); // grass tufts
      for (let i = 0; i < 3; i++) { // far birds
        const gx = Math.round(mod(t * (0.2 + i * 0.05) + i * 70 - s * 0.1, W + 30)) - 15;
        gull(ctx, gx, 8 + i * 6, ((t >> 3) + i) % 2 === 0);
      }
      return true;
    }
    default:
      return false;
  }
}
